import dayjs from 'dayjs';
import {
  activeLookupValues,
  displayForLookupCode,
  displayText,
  emptyComplaintLookups,
  firstValue,
  getRecords,
  isFieldRecord,
  lookupCode,
  lookupDisplay,
  textFromValue,
} from './complaintIntakeUtils';
import type { ComplaintCodeValue, ComplaintLookupSet, FieldRecord } from './complaintIntakeUtils';
import {
  getCaseHeader,
  getCaseHeaderTypeId,
  getCaseHeaderTypeName,
  getCaseId,
  getCaseNextPageTokenFromResponse,
  getCaseSummaryLastActivity,
  getCaseSummaryLastActivityNote,
  getCaseSummariesFromResponse,
} from './caseManagementUtils';
import type {
  ComplaintAssigneeOption,
  ComplaintAssignmentCoverageRow,
  ComplaintDashboardFilters,
  ComplaintDashboardMetrics,
  ComplaintDashboardRow,
  SlaState,
} from './complaintDashboardTypes';
import {
  createFuzzySearchIndex,
  fuzzyIndexMatch,
  fuzzyTextMatch,
  type FuzzySearchIndexEntry,
} from './complaintFuzzySearch';
import { getStateFromAddress } from './complaintStateUtils';

const today = () => dayjs().startOf('day');

const dateFromTimestamp = (value: unknown) => {
  const text = textFromValue(value);
  if (!text) return '';

  const dateOnly = text.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (dateOnly) return dateOnly;

  const parsed = dayjs(text);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : text.slice(0, 10);
};

const formatDate = (value: string) => {
  if (!value) return '';

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value;
};

const isDateInRange = (value: string, from: string, to: string) => {
  if (!value) return !from && !to;

  const parsed = dayjs(value).startOf('day');
  if (!parsed.isValid()) return false;

  if (from) {
    const fromDate = dayjs(from).startOf('day');
    if (fromDate.isValid() && parsed.isBefore(fromDate)) return false;
  }

  if (to) {
    const toDate = dayjs(to).startOf('day');
    if (toDate.isValid() && parsed.isAfter(toDate)) return false;
  }

  return true;
};

const titleFromCode = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ');

const displayRawCode = (value: string) => {
  const text = value.trim();
  if (!text) return '';

  return /[_-]/.test(text) ? titleFromCode(text) : text;
};

const unwrapResponseEnvelope = (payload: unknown): unknown => {
  if (!isFieldRecord(payload)) return payload;

  const data = payload.data || payload.Data;
  if (isFieldRecord(data)) return unwrapResponseEnvelope(data);

  const response = payload.response || payload.Response;
  if (isFieldRecord(response)) return unwrapResponseEnvelope(response);

  return payload;
};

const getLookupsSource = (payload: unknown): FieldRecord => {
  const source = unwrapResponseEnvelope(payload);
  if (!isFieldRecord(source)) return {};

  const lookups = source.lookups || source.Lookups;
  return isFieldRecord(lookups) ? lookups : source;
};

export const getDashboardLookupsFromResponse = (
  payload: unknown
): ComplaintLookupSet => {
  const source = getLookupsSource(payload);

  return {
    complaintTypes:
      (source.complaintTypes as ComplaintLookupSet['complaintTypes']) ||
      (source.complaint_types as ComplaintLookupSet['complaintTypes']) ||
      [],
    complainantTypes:
      (source.complainantTypes as ComplaintLookupSet['complainantTypes']) ||
      (source.complainant_types as ComplaintLookupSet['complainantTypes']) ||
      [],
    contactSources:
      (source.contactSources as ComplaintLookupSet['contactSources']) ||
      (source.contact_sources as ComplaintLookupSet['contactSources']) ||
      [],
    caseStatuses:
      (source.caseStatuses as ComplaintLookupSet['caseStatuses']) ||
      (source.case_statuses as ComplaintLookupSet['caseStatuses']) ||
      [],
    riskLevels:
      (source.riskLevels as ComplaintLookupSet['riskLevels']) ||
      (source.risk_levels as ComplaintLookupSet['riskLevels']) ||
      [],
    departments:
      (source.departments as ComplaintLookupSet['departments']) ||
      (source.department_values as ComplaintLookupSet['departments']) ||
      [],
    severities:
      (source.severities as ComplaintLookupSet['severities']) ||
      (source.severity_values as ComplaintLookupSet['severities']) ||
      [],
    activityTypes:
      (source.activityTypes as ComplaintLookupSet['activityTypes']) ||
      (source.activity_types as ComplaintLookupSet['activityTypes']) ||
      [],
    activityDirections:
      (source.activityDirections as ComplaintLookupSet['activityDirections']) ||
      (source.activity_directions as ComplaintLookupSet['activityDirections']) ||
      [],
    statusTransitions:
      (source.statusTransitions as ComplaintLookupSet['statusTransitions']) ||
      (source.status_transitions as ComplaintLookupSet['statusTransitions']) ||
      [],
  };
};

export const getGenericLookupGroupsFromResponse = (
  payload: unknown
): Record<string, ComplaintCodeValue[]> => {
  const source = unwrapResponseEnvelope(payload);
  if (!isFieldRecord(source)) return {};

  const rawGroups = source.lookupGroups || source.lookup_groups || source.LookupGroups;
  if (!Array.isArray(rawGroups)) return {};

  const groups: Record<string, ComplaintCodeValue[]> = {};
  rawGroups.forEach((rawGroup) => {
    if (!isFieldRecord(rawGroup)) return;
    const typeCode = textFromValue(
      rawGroup.lookupTypeCode || rawGroup.lookup_type_code || rawGroup.LookupTypeCode
    ).toUpperCase();
    const values = rawGroup.values || rawGroup.Values;
    if (typeCode && Array.isArray(values)) {
      groups[typeCode] = values.filter(isFieldRecord) as ComplaintCodeValue[];
    }
  });
  return groups;
};

const getMatterArray = (payload: unknown): FieldRecord[] => {
  const source =
    isFieldRecord(payload) && 'data' in payload ? payload.data : payload;
  if (!isFieldRecord(source)) return getRecords(source);

  const candidates = [
    source.matters,
    source.Matters,
    source.response,
    source.Response,
    source.data,
    source.Data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate
        .map((item) => getMatterRecord(item))
        .filter((item) => isFieldRecord(item));
    }

    if (isFieldRecord(candidate)) {
      const nested = getMatterArray(candidate);
      if (nested.length > 0) return nested;
    }
  }

  return [];
};

const getMatterRecord = (item: unknown): FieldRecord => {
  if (!isFieldRecord(item)) return {};

  const matter = item.matter || item.Matter;
  const context = item.context || item.Context;
  if (!isFieldRecord(matter)) return item;

  return {
    ...(isFieldRecord(context) ? context : {}),
    ...matter,
  };
};

const getDisplayForCode = (
  lookups: ComplaintLookupSet,
  lookupKey: keyof ComplaintLookupSet,
  code: string
) => {
  const values = lookups[lookupKey];
  if (!Array.isArray(values)) return titleFromCode(code);

  return displayForLookupCode(values, code) || titleFromCode(code);
};

const getSlaState = (followUpDate: string): SlaState => {
  if (!followUpDate) return 'Not Set';

  const parsed = dayjs(followUpDate).startOf('day');
  if (!parsed.isValid()) return 'Not Set';

  if (parsed.isBefore(today())) return 'Past Due';
  if (parsed.isSame(today())) return 'Due Today';
  return 'On Track';
};

const buildContextLabel = (matter: FieldRecord) => {
  const orderNumber = firstValue(matter, [
    'orderNumber',
    'OrderNumber',
    'order_number',
  ]);
  const propertyLoanId = firstValue(matter, [
    'propertyLoanId',
    'PropertyLoanId',
    'property_loan_id',
  ]);
  const spiPropertyId = firstValue(matter, [
    'spiPropertyId',
    'SpiPropertyId',
    'spi_property_id',
  ]);

  return [
    orderNumber ? `Order ${orderNumber}` : '',
    propertyLoanId ? `Property Loan ${propertyLoanId}` : '',
    spiPropertyId ? `Property ${spiPropertyId}` : '',
  ]
    .filter(Boolean)
    .join(' | ');
};

const buildClientLabel = (matter: FieldRecord) =>
  firstValue(matter, [
    'clientName',
    'ClientName',
    'client_name',
    'clientCode',
    'ClientCode',
    'client_code',
  ]);

const buildInvestorLabel = (matter: FieldRecord) =>
  firstValue(matter, [
    'investorName',
    'InvestorName',
    'investor_name',
    'investorCode',
    'InvestorCode',
    'investor_code',
  ]);

const escapeRegExp = (value: string) =>
  value.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`);

const normalizeAddressText = (value: string) =>
  value
    .replaceAll(/\s+/g, ' ')
    .replaceAll(/\b([A-Z]{2})\s+\1\b/g, '$1')
    .replaceAll(/\s+,/g, ',')
    .replaceAll(/,\s*/g, ', ')
    .replaceAll(/,+$/g, '')
    .trim();

const trimTrailingAddressParts = (value: string, parts: string[]) => {
  const cleanParts = parts
    .map((part) => normalizeAddressText(part))
    .filter(Boolean);
  if (cleanParts.length === 0) return value;

  const pattern = new RegExp(
    String.raw`[,\s]+${cleanParts
      .map((part) => escapeRegExp(part))
      .join(String.raw`(?:,?\s+)`)}\s*$`,
    'i'
  );
  return normalizeAddressText(value.replace(pattern, ''));
};

const buildAddressLabel = (matter: FieldRecord) => {
  const rawAddress = firstValue(matter, [
    'propertyAddress',
    'PropertyAddress',
    'property_address',
    'address',
    'Address',
  ]);
  const addressLine1 =
    firstValue(matter, [
      'addressLine1',
      'AddressLine1',
      'address_line1',
      'propertyAddressLine1',
      'PropertyAddressLine1',
    ]) || rawAddress;
  const state = normalizeAddressText(
    firstValue(matter, ['state', 'State', 'propertyState', 'PropertyState'])
  );
  const zip = normalizeAddressText(
    firstValue(matter, [
      'zip',
      'Zip',
      'zipCode',
      'ZipCode',
      'propertyZip',
      'PropertyZip',
    ])
  );
  const city = trimTrailingAddressParts(
    trimTrailingAddressParts(
      trimTrailingAddressParts(
        firstValue(matter, ['city', 'City', 'propertyCity', 'PropertyCity']),
        [state, zip]
      ),
      [state]
    ),
    [zip]
  );
  const street = trimTrailingAddressParts(
    trimTrailingAddressParts(
      trimTrailingAddressParts(addressLine1, [city, state, zip]),
      [city, state]
    ),
    [city, zip]
  );
  const stateZip = [state, zip].filter(Boolean).join(' ');
  const formatted = [street, city, stateZip]
    .map((part) => normalizeAddressText(part))
    .filter(Boolean)
    .join(', ');

  return formatted || normalizeAddressText(rawAddress);
};

const buildPropertyLabel = (matter: FieldRecord) =>
  buildAddressLabel(matter) ||
  buildContextLabel(matter) ||
  'Context not available';

const getCaseRowsFromSearchResponse = (
  payload: unknown,
  lookups: ComplaintLookupSet
): ComplaintDashboardRow[] =>
  getCaseSummariesFromResponse(payload)
    .map((summary) => {
      const caseRecord = getCaseHeader(summary);
      const record = (caseRecord || {}) as FieldRecord;
      const caseId = getCaseId(caseRecord);
      const dashboardCaseId = caseId ? String(caseId) : '';
      const caseTypeId = getCaseHeaderTypeId(caseRecord);
      const caseType = getCaseHeaderTypeName(caseRecord);
      const lastActivity = getCaseSummaryLastActivity(summary);
      const summaryRecord = summary as FieldRecord;
      const activityValue = (keys: string[]) =>
        firstValue(lastActivity, keys) || firstValue(summaryRecord, keys);
      const activityTimestamp = (keys: string[]) => {
        const raw = keys
          .map((key) => lastActivity?.[key] || summaryRecord[key])
          .find((value) => value !== undefined && value !== null);
        const text = textFromValue(raw);
        if (text) return text;
        if (!isFieldRecord(raw)) return '';

        const seconds = Number(textFromValue(raw.seconds || raw.Seconds));
        const nanos = Number(textFromValue(raw.nanos || raw.Nanos));
        return Number.isFinite(seconds)
          ? new Date(seconds * 1000 + (Number.isFinite(nanos) ? nanos / 1_000_000 : 0)).toISOString()
          : '';
      };
      const statusCode = firstValue(record, [
        'caseStatusCode',
        'CaseStatusCode',
        'case_status_code',
      ]);
      const riskLevelCode = firstValue(record, [
        'risk_level_code',
        'riskLevelCode',
        'RiskLevelCode',
      ]);
      const workTypeCode = firstValue(record, [
        'work_type_code',
        'workTypeCode',
        'WorkTypeCode',
      ]);
      const workType = firstValue(record, [
        'workType',
        'WorkType',
        'work_type',
      ]);
      const complaintTypeCode = firstValue(record, [
        'complaint_type_code',
        'complaintTypeCode',
        'ComplaintTypeCode',
      ]);
      const followUpDate = dateFromTimestamp(
        firstValue(record, ['followUpDt', 'FollowUpDt', 'follow_up_dt'])
      );
      const completeDate = dateFromTimestamp(
        firstValue(record, ['completeDt', 'CompleteDt', 'complete_dt'])
      );
      const openedDate = dateFromTimestamp(
        firstValue(record, ['openDt', 'OpenDt', 'open_dt'])
      );
      const propertyId = firstValue(record, [
        'propertyId',
        'PropertyId',
        'property_id',
      ]);
      const orderNumber = firstValue(record, [
        'orderNumber',
        'OrderNumber',
        'order_number',
      ]);
      const propertyLoanId = firstValue(record, [
        'propertyLoanId',
        'PropertyLoanId',
        'property_loan_id',
      ]);
      const caseContextRecord = {
        ...record,
        spiPropertyId: propertyId,
        orderNumber,
        propertyLoanId,
      };

      return {
        complaintMatterId: dashboardCaseId,
        caseId: dashboardCaseId,
        caseTypeId: caseTypeId ? String(caseTypeId) : '',
        caseType,
        rowVersion: firstValue(record, ['modifyDt', 'ModifyDt', 'modify_dt']),
        propertyLabel: buildPropertyLabel(caseContextRecord),
        contextLabel: buildContextLabel(caseContextRecord),
        clientLabel: buildClientLabel(caseContextRecord),
        investorLabel: buildInvestorLabel(caseContextRecord),
        workType: workType || displayRawCode(workTypeCode) || caseType,
        complaintType:
          getDisplayForCode(lookups, 'complaintTypes', complaintTypeCode) ||
          caseType,
        status: getDisplayForCode(lookups, 'caseStatuses', statusCode),
        statusCode,
        riskLevel: getDisplayForCode(lookups, 'riskLevels', riskLevelCode),
        riskLevelCode,
        followUpDate,
        followUpDisplay: formatDate(followUpDate),
        slaState: getSlaState(followUpDate),
        assignedTo: firstValue(record, [
          'assignedToUser',
          'AssignedToUser',
          'assigned_to_user',
        ]),
        assignedUserId: firstValue(record, [
          'assigned_user_id',
          'assignedUserId',
          'AssignedUserId',
        ]),
        receivedDate: formatDate(
          dateFromTimestamp(
            firstValue(record, [
              'received_date',
              'receivedDate',
              'ReceivedDate',
            ])
          )
        ),
        openedDate,
        openedDisplay: formatDate(openedDate),
        completeDate,
        completeDisplay: formatDate(completeDate),
        lastActivityNote: getCaseSummaryLastActivityNote(summary),
        lastActivityTypeCode: activityValue([
          'activityTypeCode',
          'ActivityTypeCode',
          'activity_type_code',
        ]),
        lastActivityCreatedBy: activityValue([
          'createdBy',
          'CreatedBy',
          'created_by',
        ]),
        lastActivityCreateDt: activityTimestamp([
          'createDt',
          'CreateDt',
          'create_dt',
        ]),
        vendorCode: firstValue(record, [
          'vendorCode',
          'VendorCode',
          'vendor_code',
        ]),
        orderNumber,
        propertyLoanId,
      };
    })
    .filter((row) => row.complaintMatterId);

export const getComplaintRowsFromSearchResponse = (
  payload: unknown,
  lookups: ComplaintLookupSet = emptyComplaintLookups
): ComplaintDashboardRow[] => {
  const caseRows = getCaseRowsFromSearchResponse(payload, lookups);
  if (caseRows.length > 0) return caseRows;

  return getMatterArray(payload)
    .map((matter) => {
      const statusCode = firstValue(matter, [
        'caseStatusCode',
        'CaseStatusCode',
        'case_status_code',
      ]);
      const riskLevelCode = firstValue(matter, [
        'riskLevelCode',
        'RiskLevelCode',
        'risk_level_code',
      ]);
      const workTypeCode = firstValue(matter, [
        'workTypeCode',
        'WorkTypeCode',
        'work_type_code',
      ]);
      const workType = firstValue(matter, [
        'workType',
        'WorkType',
        'work_type',
      ]);
      const complaintTypeCode = firstValue(matter, [
        'complaintTypeCode',
        'ComplaintTypeCode',
        'complaint_type_code',
      ]);
      const followUpDate = dateFromTimestamp(
        firstValue(matter, ['followUpDate', 'FollowUpDate', 'follow_up_date'])
      );
      const openedDate = dateFromTimestamp(
        firstValue(matter, ['createDt', 'CreateDt', 'create_dt'])
      );
      const completeDate = dateFromTimestamp(
        firstValue(matter, [
          'closedDt',
          'ClosedDt',
          'closed_dt',
          'completeDt',
          'CompleteDt',
          'complete_dt',
        ])
      );

      return {
        complaintMatterId: firstValue(matter, [
          'complaintMatterId',
          'ComplaintMatterId',
          'complaint_matter_id',
        ]),
        caseId: '',
        caseTypeId: '',
        caseType: 'Complaints',
        rowVersion: firstValue(matter, [
          'rowVersion',
          'RowVersion',
          'row_version',
        ]),
        propertyLabel: buildPropertyLabel(matter),
        contextLabel: buildContextLabel(matter),
        clientLabel: buildClientLabel(matter),
        investorLabel: buildInvestorLabel(matter),
        workType: workType || displayRawCode(workTypeCode),
        complaintType: getDisplayForCode(
          lookups,
          'complaintTypes',
          complaintTypeCode
        ),
        status: getDisplayForCode(lookups, 'caseStatuses', statusCode),
        statusCode,
        riskLevel: getDisplayForCode(lookups, 'riskLevels', riskLevelCode),
        riskLevelCode,
        followUpDate,
        followUpDisplay: formatDate(followUpDate),
        slaState: getSlaState(followUpDate),
        assignedTo: firstValue(matter, [
          'assignedToUser',
          'AssignedToUser',
          'assigned_to_user',
        ]),
        assignedUserId: firstValue(matter, [
          'assignedUserId',
          'AssignedUserId',
          'assigned_user_id',
        ]),
        receivedDate: formatDate(
          dateFromTimestamp(
            firstValue(matter, [
              'receivedDate',
              'ReceivedDate',
              'received_date',
            ])
          )
        ),
        openedDate,
        openedDisplay: formatDate(openedDate),
        completeDate,
        completeDisplay: formatDate(completeDate),
        lastActivityNote: firstValue(matter, [
          'actionSteps',
          'ActionSteps',
          'action_steps',
        ]),
        lastActivityTypeCode: '',
        lastActivityCreatedBy: '',
        lastActivityCreateDt: '',
        vendorCode: firstValue(matter, [
          'vendorCode',
          'VendorCode',
          'vendor_code',
        ]),
        orderNumber: firstValue(matter, [
          'orderNumber',
          'OrderNumber',
          'order_number',
        ]),
        propertyLoanId: firstValue(matter, [
          'propertyLoanId',
          'PropertyLoanId',
          'property_loan_id',
        ]),
      };
    })
    .filter((row) => row.complaintMatterId);
};

export const getNextPageTokenFromSearchResponse = (payload: unknown) => {
  const casePageToken = getCaseNextPageTokenFromResponse(payload);
  if (casePageToken) return casePageToken;

  const source =
    isFieldRecord(payload) && 'data' in payload ? payload.data : payload;
  if (!isFieldRecord(source)) return '';

  return firstValue(source, [
    'nextPageToken',
    'NextPageToken',
    'next_page_token',
  ]);
};

export const buildVisibleMetrics = (
  rows: ComplaintDashboardRow[]
): ComplaintDashboardMetrics => {
  const openRows = rows.filter((row) => !isTerminalComplaintRow(row));

  return {
    open: openRows.length,
    dueToday: openRows.filter((row) => row.slaState === 'Due Today').length,
    pastDue: openRows.filter((row) => row.slaState === 'Past Due').length,
    criticalOrHigh: openRows.filter((row) =>
      ['CRITICAL', 'HIGH'].includes(row.riskLevelCode.toUpperCase())
    ).length,
    unassigned: openRows.filter((row) => isUnassignedComplaintRow(row)).length,
  };
};

export const isTerminalComplaintRow = (row: ComplaintDashboardRow) =>
  ['CLOSED', 'RESOLVED'].includes(row.statusCode.toUpperCase());

const normalizeComplaintUser = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const withoutDomain = trimmed.includes('@') ? trimmed.split('@')[0] : trimmed;
  const withoutSlash = withoutDomain.includes('\\')
    ? withoutDomain.split('\\').pop() || ''
    : withoutDomain;

  return withoutSlash.trim();
};

const normalizeAssignee = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/@[^\s@]+$/, '');

export const getDefaultComplaintUser = () => '';

export const getComplaintUserFromToken = (token?: string) => {
  if (!token) return getDefaultComplaintUser();

  const payloadSegment = token
    .trim()
    .replace(/^bearer\s+/i, '')
    .split('.')[1];
  if (!payloadSegment) return getDefaultComplaintUser();

  try {
    const base64 = payloadSegment.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    if (typeof globalThis === 'undefined' || !globalThis.atob) {
      return getDefaultComplaintUser();
    }

    const payload = JSON.parse(globalThis.atob(padded));
    const user = normalizeComplaintUser(
      firstValue(payload, [
        'safeviewUserId',
        'SafeViewUserId',
        'samAccountName',
        'onPremisesSamAccountName',
        'preferred_username',
        'upn',
        'userPrincipalName',
        'unique_name',
        'email',
        'user_name',
        'username',
        'nameid',
        'sub',
      ])
    );
    return user || getDefaultComplaintUser();
  } catch {
    return getDefaultComplaintUser();
  }
};

export const getComplaintHeaderUser = (user: string) => user.trim();

export const getComplaintHeaderInitials = (user: string) =>
  getComplaintHeaderUser(user)
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

export const isAssignedToComplaintUser = (
  row: ComplaintDashboardRow,
  user: string
) => normalizeAssignee(row.assignedTo) === normalizeAssignee(user);

const hasAssignedUserId = (assignedUserId: string) => {
  const normalized = assignedUserId.trim();
  return Boolean(normalized && normalized !== '0');
};

export const isUnassignedComplaintRow = (row: ComplaintDashboardRow) =>
  !row.assignedTo.trim() && !hasAssignedUserId(row.assignedUserId);

export const buildAssigneeOptions = (
  directoryUsers: ComplaintAssigneeOption[] = []
): ComplaintAssigneeOption[] => {
  const options = new Map<string, ComplaintAssigneeOption>();
  const addOption = (
    safeviewUserId: string,
    label: string,
    assignedUserId: string,
    source: ComplaintAssigneeOption['source']
  ) => {
    const normalized = normalizeAssignee(safeviewUserId);
    if (!normalized) return;

    if (options.has(normalized)) return;

    options.set(normalized, {
      safeviewUserId: safeviewUserId.trim(),
      label: label.trim() || safeviewUserId.trim(),
      assignedUserId: assignedUserId.trim(),
      source,
    });
  };

  for (const user of directoryUsers) {
    addOption(user.safeviewUserId, user.label, '', 'directory');
  }

  return Array.from(options.values()).sort((first, second) =>
    first.label.localeCompare(second.label)
  );
};

export const getUniqueValues = (
  rows: ComplaintDashboardRow[],
  key: keyof ComplaintDashboardRow
) =>
  Array.from(
    new Set(rows.map((row) => String(row[key] || '').trim()).filter(Boolean))
  ).sort((first, second) => first.localeCompare(second));

export const buildAssignmentCoverageRows = (
  rows: ComplaintDashboardRow[]
): ComplaintAssignmentCoverageRow[] => {
  const coverage = new Map<string, ComplaintAssignmentCoverageRow>();

  for (const row of rows.filter((item) => !isTerminalComplaintRow(item))) {
    const assignee = row.assignedTo.trim() || 'Unassigned';
    const taskType = row.workType || row.complaintType || 'Not set';
    const key = `${assignee}|${taskType}`;
    const existing = coverage.get(key) || {
      assignee,
      taskType,
      clients: [],
      states: [],
      activeCount: 0,
      dueTodayCount: 0,
      pastDueCount: 0,
    };
    const client = row.clientLabel.trim();
    const state = getStateFromAddress(row.propertyLabel);

    coverage.set(key, {
      ...existing,
      clients:
        client && !existing.clients.includes(client)
          ? [...existing.clients, client].sort()
          : existing.clients,
      states:
        state && !existing.states.includes(state)
          ? [...existing.states, state].sort()
          : existing.states,
      activeCount: existing.activeCount + 1,
      dueTodayCount:
        existing.dueTodayCount + (row.slaState === 'Due Today' ? 1 : 0),
      pastDueCount:
        existing.pastDueCount + (row.slaState === 'Past Due' ? 1 : 0),
    });
  }

  return Array.from(coverage.values()).sort((first, second) =>
    `${first.assignee} ${first.taskType}`.localeCompare(
      `${second.assignee} ${second.taskType}`
    )
  );
};

const getComplaintRowSearchText = (row: ComplaintDashboardRow) =>
  [
    row.complaintMatterId,
    row.propertyLabel,
    row.contextLabel,
    row.clientLabel,
    row.investorLabel,
    row.workType,
    row.complaintType,
    row.caseType,
    row.status,
    row.riskLevel,
    row.assignedTo,
    row.vendorCode,
    row.orderNumber,
    row.propertyLoanId,
  ].join(' ');

export const buildComplaintRowSearchIndex = (rows: ComplaintDashboardRow[]) =>
  createFuzzySearchIndex(rows, getComplaintRowSearchText);

export const filterComplaintRows = (
  rows: ComplaintDashboardRow[],
  filters: ComplaintDashboardFilters,
  rowSearchIndex?: Array<FuzzySearchIndexEntry<ComplaintDashboardRow>>
) => {
  const search = filters.search.trim();
  const effectiveRowSearchIndex =
    search && rowSearchIndex?.length === rows.length
      ? rowSearchIndex
      : undefined;

  return rows.filter((row, index) => {
    if (!filters.status && isTerminalComplaintRow(row)) return false;

    const searchMatch =
      !search ||
      (effectiveRowSearchIndex
        ? fuzzyIndexMatch(search, effectiveRowSearchIndex[index])
        : fuzzyTextMatch(search, getComplaintRowSearchText(row)));

    return (
      searchMatch &&
      (!filters.workType || row.workType === filters.workType) &&
      (!filters.status || row.statusCode === filters.status) &&
      (!filters.client || row.clientLabel === filters.client) &&
      (!filters.investor || row.investorLabel === filters.investor) &&
      (!filters.riskLevel || row.riskLevelCode === filters.riskLevel) &&
      (!filters.assignee || row.assignedTo === filters.assignee) &&
      (!filters.sla || row.slaState === filters.sla) &&
      isDateInRange(row.openedDate, filters.openedFrom, filters.openedTo)
    );
  });
};

export const statusOptionsFromLookups = (lookups: ComplaintLookupSet) =>
  activeLookupValues(lookups.caseStatuses).map((status) => ({
    code: lookupCode(status),
    label: lookupDisplay(status),
  }));

export const riskOptionsFromLookups = (lookups: ComplaintLookupSet) =>
  activeLookupValues(lookups.riskLevels).map((risk) => ({
    code: lookupCode(risk),
    label: lookupDisplay(risk),
  }));

const csvEscape = (value: unknown) => {
  const text = textFromValue(value);
  if (!/[\n",]/.test(text)) return text;

  return `"${text.replaceAll('"', '""')}"`;
};

export const MANAGER_CSV_COLUMNS = [
  'Case Number',
  'Client',
  'Case Type',
  'Case Status',
  'Open Date',
  'Follow up Date',
  'Complete Date',
  'Assigned User',
  'Last Activity Note',
];

const managerCsvValueByColumn: Record<
  string,
  (row: ComplaintDashboardRow) => unknown
> = {
  'Case Number': (row) => row.caseId || row.complaintMatterId,
  Client: (row) => row.clientLabel,
  'Case Type': (row) => row.caseType,
  'Case Status': (row) => row.status,
  'Open Date': (row) => row.openedDisplay,
  'Follow up Date': (row) => row.followUpDisplay,
  'Complete Date': (row) => row.completeDisplay,
  'Assigned User': (row) => row.assignedTo,
  'Last Activity Note': (row) => row.lastActivityNote,
};

export const buildManagerCsv = (
  rows: ComplaintDashboardRow[],
  selectedColumns = MANAGER_CSV_COLUMNS
) => {
  const columns = selectedColumns.filter(
    (column) => managerCsvValueByColumn[column]
  );
  const effectiveColumns = columns.length > 0 ? columns : MANAGER_CSV_COLUMNS;
  const body = rows.map((row) =>
    effectiveColumns.map((column) => managerCsvValueByColumn[column](row))
  );

  return [effectiveColumns, ...body]
    .map((columns) => columns.map((column) => csvEscape(column)).join(','))
    .join('\n');
};

export const displayValue = (value: unknown) => {
  const text = displayText(value, 'Not Available');
  return text === '[object Object]' ? 'Not Available' : text;
};
